import { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Upload, Eye, Download, PenTool, Trash2, Loader2 } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import { PohledPill } from '../../layout/ListToolbar';
import { SECTION_COLOR, datum } from '../lide';
import { Sekce, Pole, Vstup, Vyber, Zaskrtavatko, Tlacitko, PotvrzeniModal, Hlaska, muted, linka } from './ui';

/**
 * Karta zaměstnance → Dokumenty.
 *
 * Řádky místo karet s barevným okrajem, podpis jako text (červeně jen „čeká"),
 * nahrání v `ModalShell` (dřív formulář rozbalený nad seznamem), smazání přes
 * potvrzovací modal a na mobilu vůbec (norma §1b — mazání není editace).
 */

const DEN = 86400000;

const velikost = (b) => {
    if (!b) return null;
    if (b < 1048576) return `${Math.round(b / 1024)} kB`;
    return `${(b / 1048576).toFixed(1)} MB`;
};

const EXT = {
    'application/pdf': '.pdf', 'image/jpeg': '.jpg,.jpeg', 'image/png': '.png', 'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
};

const PRAZDNY_UPLOAD = { nazev: '', popis: '', typ_dokumentu_id: '', expiruje_at: '', soubor: null, uz_podepsano: false };

export default function DokumentyZamestnance({ zamestnanecId, isDark, isMobile, muzuEditovat, onPocet, onZmena }) {
    const [dokumenty, setDokumenty] = useState(null);
    const [typy, setTypy] = useState([]);
    const [pohled, setPohled] = useState('platne');
    const [upload, setUpload] = useState(null);
    const [nahrava, setNahrava] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [podepisuje, setPodepisuje] = useState(null);
    const [smazat, setSmazat] = useState(null);
    const souborRef = useRef(null);
    const mut = muted(isDark);

    const nacti = useCallback(() => {
        fetch(`/api/zamestnanci/${zamestnanecId}/dokumenty`).then(r => r.json())
            .then(d => { const s = Array.isArray(d) ? d : []; setDokumenty(s); onPocet?.(s.length); })
            .catch(() => setDokumenty([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zamestnanecId]);

    useEffect(() => { setDokumenty(null); setPohled('platne'); nacti(); }, [nacti]);

    const otevriUpload = () => {
        setUpload({ ...PRAZDNY_UPLOAD }); setChyba(null);
        if (!typy.length) fetch('/api/ciselniky/typy-dokumentu').then(r => r.json()).then(d => setTypy(Array.isArray(d) ? d : [])).catch(() => {});
    };

    if (dokumenty === null) {
        return <div className="flex justify-center" style={{ padding: 32, color: mut }}><Loader2 size={18} className="animate-spin" /></div>;
    }

    const dnes = new Date();
    const expirovany = (d) => d.expiruje_at && new Date(d.expiruje_at) < dnes;
    const platne = dokumenty.filter(d => !expirovany(d));
    const expirovane = dokumenty.filter(expirovany);
    const zobrazene = pohled === 'platne' ? platne : expirovane;
    const skupiny = Object.entries(zobrazene.reduce((acc, d) => {
        const k = d.typ_dokumentu?.nazev || 'Ostatní';
        (acc[k] = acc[k] || []).push(d);
        return acc;
    }, {})).sort(([a], [b]) => a.localeCompare(b, 'cs'));

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const podepsat = async (dok) => {
        setPodepisuje(dok.id); setChyba(null);
        const res = await fetch(`/api/dokumenty/${dok.id}/podepsat-zamestnavatel`, { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } });
        setPodepisuje(null);
        if (res.ok) nacti();
        else { const d = await res.json().catch(() => ({})); setChyba({ typ: 'chyba', text: d.error || 'Podpis selhal.' }); }
    };

    const nahrat = async () => {
        setNahrava(true); setChyba(null);
        const fd = new FormData();
        fd.append('nazev', upload.nazev); fd.append('popis', upload.popis);
        if (upload.typ_dokumentu_id) fd.append('typ_dokumentu_id', upload.typ_dokumentu_id);
        if (upload.expiruje_at) fd.append('expiruje_at', upload.expiruje_at);
        if (upload.uz_podepsano) fd.append('uz_podepsano', '1');
        fd.append('soubor', upload.soubor);
        const res = await fetch(`/api/zamestnanci/${zamestnanecId}/dokumenty`, { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() }, body: fd });
        setNahrava(false);
        if (res.ok) { setUpload(null); nacti(); onZmena?.(); }
        else { const e = await res.json().catch(() => ({})); setChyba({ typ: 'chyba', text: e.error || e.message || 'Chyba při nahrávání' }); }
    };

    const typUploadu = typy.find(t => String(t.id) === String(upload?.typ_dokumentu_id));
    const accept = typUploadu?.povolene_mime
        ? typUploadu.povolene_mime.map(m => EXT[m] || '').filter(Boolean).join(',')
        : '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.txt';

    return (
        <>
            <div className="flex items-center" style={{ gap: 8, padding: '10px 0 2px', flexWrap: 'wrap' }}>
                <PohledPill label="Platné" pocet={platne.length} aktivni={pohled === 'platne'} onClick={() => setPohled('platne')}
                    barva={SECTION_COLOR} isDark={isDark} mutedColor={mut} kompaktni={isMobile} />
                {expirovane.length > 0 && (
                    <PohledPill label="Expirované" pocet={expirovane.length} aktivni={pohled === 'expirovane'} onClick={() => setPohled('expirovane')}
                        barva={SECTION_COLOR} isDark={isDark} mutedColor={mut} kompaktni={isMobile} />
                )}
                <div style={{ flex: 1 }} />
                {muzuEditovat && (
                    <Tlacitko isDark={isDark} primarni vyska={isMobile ? 40 : 32} onClick={otevriUpload}>
                        <Upload size={14} /> Nahrát dokument
                    </Tlacitko>
                )}
            </div>
            {!upload && <Hlaska hlaska={chyba} />}

            {zobrazene.length === 0 && (
                <p className="text-[13px] text-center" style={{ color: mut, padding: '28px 0' }}>
                    {dokumenty.length === 0 ? 'Žádné dokumenty.' : 'V tomhle pohledu nic není.'}
                </p>
            )}

            {skupiny.map(([typ, docs]) => (
                <Sekce key={typ} nazev={`${typ} · ${docs.length}`} isDark={isDark}>
                    {docs.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)).map(d => {
                        const ceka = d.vyzaduje_podpis && !d.podepsano_at;
                        const dni = ceka && d.uploaded_at ? Math.floor((dnes - new Date(d.uploaded_at)) / DEN) : null;
                        const expDni = d.expiruje_at ? Math.ceil((new Date(d.expiruje_at) - dnes) / DEN) : null;
                        const podpisFirmy = d.vyzaduje_podpis && !d.podpis_zamestnavatel_at;
                        const meta = [
                            d.soubor_nazev, velikost(d.soubor_velikost),
                            d.uploaded_at ? `nahráno ${datum(d.uploaded_at)}` : null,
                            d.expiruje_at ? (expDni < 0 ? `expiroval ${datum(d.expiruje_at)}` : `platí do ${datum(d.expiruje_at)}`) : null,
                        ].filter(Boolean).join(' · ');
                        return (
                            <div key={d.id} className={isMobile ? 'flex flex-col' : 'flex items-center'}
                                style={{ gap: isMobile ? 8 : 12, padding: '10px 0', borderBottom: `1px solid ${linka(isDark)}` }}>
                                <div className="flex items-start min-w-0 flex-1" style={{ gap: 10 }}>
                                    <FileText size={17} style={{ color: mut, flexShrink: 0, marginTop: 1 }} />
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{d.nazev}</p>
                                        <p className="text-[12px]" style={{ color: mut, overflowWrap: 'anywhere' }}>{meta}</p>
                                        {d.vyzaduje_podpis && (
                                            <p className="text-[12px]" style={{ marginTop: 2 }}>
                                                {ceka
                                                    ? <span style={{ color: '#f87171', fontWeight: 600 }}>zaměstnanec nepodepsal{dni != null ? ` · čeká ${dni} d` : ''}</span>
                                                    : <span style={{ color: 'var(--text-secondary)' }}>podepsal {datum(d.podepsano_at)}</span>}
                                                <span style={{ color: mut }}>
                                                    {' · '}{d.podpis_zamestnavatel_at ? `firma podepsala ${datum(d.podpis_zamestnavatel_at)}` : 'firma zatím nepodepsala'}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center flex-wrap" style={{ gap: 6, paddingLeft: isMobile ? 27 : 0 }}>
                                    {d.soubor_mime === 'application/pdf' && (
                                        <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 30} href={`/api/dokumenty/${d.id}/preview`} title="Náhled"><Eye size={14} />{isMobile && ' Náhled'}</Tlacitko>
                                    )}
                                    <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 30} href={`/api/dokumenty/${d.id}/download`} title="Stáhnout"><Download size={14} />{isMobile && ' Stáhnout'}</Tlacitko>
                                    {muzuEditovat && podpisFirmy && d.soubor_mime === 'application/pdf' && (
                                        <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 30} onClick={() => podepsat(d)} disabled={podepisuje === d.id}>
                                            <PenTool size={14} /> {podepisuje === d.id ? 'Podepisuji…' : 'Podepsat za firmu'}
                                        </Tlacitko>
                                    )}
                                    {muzuEditovat && !isMobile && (
                                        <button type="button" title="Smazat dokument" aria-label={`Smazat ${d.nazev}`} onClick={() => setSmazat(d)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mut, padding: 6, display: 'flex' }}>
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </Sekce>
            ))}

            {upload && (
                <ModalShell onClose={() => setUpload(null)} isDark={isDark} barva={SECTION_COLOR} ikona={Upload} nadpis="Nahrát dokument" sirka={520}
                    paticka={(
                        <div className="flex gap-2 justify-end">
                            <Tlacitko isDark={isDark} vyska={40} onClick={() => setUpload(null)}>Zrušit</Tlacitko>
                            <Tlacitko isDark={isDark} vyska={40} primarni onClick={nahrat} disabled={nahrava || !upload.nazev || !upload.soubor}>
                                {nahrava ? 'Nahrávám…' : 'Nahrát'}
                            </Tlacitko>
                        </div>
                    )}>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <Pole label="Název *" isDark={isDark} siroky>
                            <Vstup isDark={isDark} value={upload.nazev} placeholder="Název dokumentu" onChange={e => setUpload(u => ({ ...u, nazev: e.target.value }))} />
                        </Pole>
                        <Pole label="Typ dokumentu" isDark={isDark}>
                            <Vyber isDark={isDark} value={upload.typ_dokumentu_id} placeholder="— Vyberte typ —"
                                onChange={v => setUpload(u => ({ ...u, typ_dokumentu_id: v }))}
                                options={typy.map(t => ({ value: t.id, label: t.nazev }))} />
                        </Pole>
                        <Pole label="Expiruje dne" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={upload.expiruje_at} onChange={e => setUpload(u => ({ ...u, expiruje_at: e.target.value }))} />
                        </Pole>
                        <Pole label="Popis" isDark={isDark} siroky>
                            <Vstup isDark={isDark} value={upload.popis} placeholder="Volitelný popis" onChange={e => setUpload(u => ({ ...u, popis: e.target.value }))} />
                        </Pole>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Zaskrtavatko isDark={isDark} checked={upload.uz_podepsano} onChange={v => setUpload(u => ({ ...u, uz_podepsano: v }))}
                                label="Už podepsáno fyzicky (sken papírové smlouvy s podpisy)" />
                            {typUploadu && !upload.uz_podepsano && (typUploadu.vyzaduje_podpis_zamestnanec || typUploadu.vyzaduje_podpis_zamestnavatel) && (
                                <p className="text-[12px]" style={{ color: mut }}>
                                    Vyžaduje podpis {[
                                        typUploadu.vyzaduje_podpis_zamestnanec ? 'zaměstnance' : null,
                                        typUploadu.vyzaduje_podpis_zamestnavatel ? `zaměstnavatele${typUploadu.podpis_zamestnavatel_auto ? ' (automaticky)' : ''}` : null,
                                    ].filter(Boolean).join(' a ')}.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center" style={{ gridColumn: '1 / -1', gap: 10 }}>
                            <input ref={souborRef} type="file" accept={accept} className="hidden"
                                onChange={e => setUpload(u => ({ ...u, soubor: e.target.files[0] || null }))} />
                            <Tlacitko isDark={isDark} vyska={40} onClick={() => souborRef.current?.click()}><Upload size={14} /> Vybrat soubor</Tlacitko>
                            <span className="text-[12.5px] truncate" style={{ color: upload.soubor ? 'var(--text-primary)' : mut }}>
                                {upload.soubor ? upload.soubor.name : 'max 10 MB'}
                            </span>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}><Hlaska hlaska={chyba} /></div>
                    </div>
                </ModalShell>
            )}

            {smazat && (
                <PotvrzeniModal isDark={isDark} onZavri={() => setSmazat(null)} nadpis="Smazat dokument?"
                    text={`„${smazat.nazev}" se smaže i se souborem. Nejde to vrátit.`}
                    onPotvrdit={async () => {
                        const res = await fetch(`/api/dokumenty/${smazat.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } });
                        if (res.ok) { nacti(); onZmena?.(); }
                        else { const e = await res.json().catch(() => ({})); setChyba({ typ: 'chyba', text: e.error || 'Nelze smazat' }); }
                        setSmazat(null);
                    }} />
            )}
        </>
    );
}
